/**
 * Profile Goals Section — shows user's goals grouped by status.
 * Privacy: private goals show as "🔒 Maxfiy maqsad" for non-owners.
 */

import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Target, Lock, CheckCircle2, Clock, Calendar, Trophy } from 'lucide-react'
import { useAuthStore } from '@turnifly/shared/stores'
import { useProfileGoals } from '@turnifly/shared/hooks'
import { useCompletedChallenges } from '@turnifly/shared/hooks'
import { calculateProgress, getProgressColor, getProgressBarVariant } from '@turnifly/shared/utils'
import { ProgressBar } from '@turnifly/shared/ui'
import { Skeleton } from '@turnifly/shared/ui'
import type { Goal } from '@turnifly/shared/types'

interface ProfileGoalsSectionProps {
  userId: string
}

export const ProfileGoalsSection: React.FC<ProfileGoalsSectionProps> = ({ userId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const currentUserId = useAuthStore((s) => s.user?.id)
  const { data: goals, isLoading } = useProfileGoals(userId)
  const { data: completedChallenges } = useCompletedChallenges(userId)
  const isOwner = currentUserId === userId

  const grouped = useMemo(() => {
    if (!goals) return { inProgress: [], planned: [], achieved: [] }
    return {
      inProgress: goals.filter((g) => g.status === 'in_progress'),
      planned: goals.filter((g) => g.status === 'planned'),
      achieved: goals.filter((g) => g.status === 'achieved'),
    }
  }, [goals])

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 rounded-xl" />
        <Skeleton className="h-10 rounded-xl" />
      </div>
    )
  }

  const allGoals = [...grouped.inProgress, ...grouped.planned, ...grouped.achieved]
  const hasGoals = allGoals.length > 0
  const hasChallenges = (completedChallenges ?? []).length > 0

  if (!hasGoals && !hasChallenges) {
    return (
      <div className="flex flex-col items-center py-4 text-center">
        <Target size={24} className="text-text-hint/30 mb-2" />
        <p className="text-xs text-text-hint">{t('profile.noGoals')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {grouped.inProgress.length > 0 && (
        <GoalGroup
          title={t('profile.goalsInProgress')}
          count={grouped.inProgress.length}
          icon={<Clock size={12} className="text-primary" />}
          goals={grouped.inProgress}
          isOwner={isOwner}
        />
      )}

      {grouped.planned.length > 0 && (
        <GoalGroup
          title={t('profile.goalsPlanned')}
          count={grouped.planned.length}
          icon={<Calendar size={12} className="text-text-hint" />}
          goals={grouped.planned}
          isOwner={isOwner}
        />
      )}

      {grouped.achieved.length > 0 && (
        <GoalGroup
          title={t('profile.goalsAchieved')}
          count={grouped.achieved.length}
          icon={<CheckCircle2 size={12} className="text-success" />}
          goals={grouped.achieved}
          isOwner={isOwner}
        />
      )}

      {/* Completed Challenges */}
      {hasChallenges && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Trophy size={12} className="text-accent" />
            <span className="text-[11px] font-medium text-text-hint">
              {t('profile.completedChallenges')} ({completedChallenges!.length})
            </span>
          </div>
          <div className="space-y-1.5">
            {completedChallenges!.map((ch) => (
              <button
                key={ch.id}
                onClick={() => navigate(`/challenges/${ch.id}`)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left tap-highlight-transparent ${
                  ch.is_hidden
                    ? 'bg-white/[0.02]'
                    : 'bg-accent/[0.04] border border-accent/[0.08]'
                }`}
              >
                <span className="text-sm shrink-0">
                  {ch.is_hidden ? '🔒' : (ch.cover_emoji ?? '🏆')}
                </span>
                <div className="flex-1 min-w-0">
                  <span className={`text-xs font-medium truncate block ${
                    ch.is_hidden ? 'text-text-hint italic' : 'text-text'
                  }`}>
                    {ch.is_hidden ? t('profile.privateChallenge') : ch.name}
                  </span>
                  <span className="text-[10px] text-accent/60">
                    {Math.round(ch.completion_rate)}% · {ch.duration_days} {t('profile.days')}
                  </span>
                </div>
                <Trophy size={12} className="text-accent/40 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Goal Group ──

interface GoalGroupProps {
  title: string
  count: number
  icon: React.ReactNode
  goals: Goal[]
  isOwner: boolean
}

const GoalGroup: React.FC<GoalGroupProps> = ({ title, count, icon, goals, isOwner }) => (
  <div>
    <div className="flex items-center gap-1.5 mb-1.5">
      {icon}
      <span className="text-[11px] font-medium text-text-hint">
        {title} ({count})
      </span>
    </div>
    <div className="space-y-1.5">
      {goals.map((goal) => (
        <ProfileGoalRow key={goal.id} goal={goal} isOwner={isOwner} />
      ))}
    </div>
  </div>
)

// ── Single Goal Row ──

interface ProfileGoalRowProps {
  goal: Goal
  isOwner: boolean
}

const ProfileGoalRow: React.FC<ProfileGoalRowProps> = React.memo(({ goal, isOwner }) => {
  const { t } = useTranslation()
  const isPrivateForViewer = !isOwner && goal.is_private
  const progress = calculateProgress(goal)
  const progressColor = getProgressColor(progress)
  const barVariant = getProgressBarVariant(progress)
  const isAchieved = goal.status === 'achieved'

  const displayName = isPrivateForViewer
    ? t('profile.privateGoal')
    : goal.name

  const displayIcon = isPrivateForViewer ? '🔒' : goal.icon

  return (
    <div className={`flex items-center gap-2.5 px-3 py-2 rounded-xl ${
      isAchieved
        ? 'bg-success/[0.04] border border-success/[0.08]'
        : 'bg-white/[0.02]'
    }`}>
      <span className="text-sm shrink-0">{displayIcon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-medium truncate ${
            isPrivateForViewer ? 'text-text-hint italic' : 'text-text'
          }`}>
            {displayName}
          </span>
          {isAchieved && <CheckCircle2 size={12} className="text-success shrink-0" />}
          {isPrivateForViewer && <Lock size={10} className="text-text-hint/50 shrink-0" />}
        </div>
        {/* Progress bar for active goals */}
        {!isAchieved && (
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1">
              <ProgressBar value={progress} variant={barVariant} />
            </div>
            <span className={`text-[10px] font-semibold tabular-nums shrink-0 ${progressColor}`}>
              {progress}%
            </span>
          </div>
        )}
        {/* Year for achieved */}
        {isAchieved && (
          <span className="text-[10px] text-success/60">{goal.year}</span>
        )}
      </div>
    </div>
  )
})

ProfileGoalRow.displayName = 'ProfileGoalRow'
